import { ApiError, problemContext } from '@/api/client';
import { cn } from '@/lib/utils';
import { useWording } from '@/lib/useAudience';
import { Icon } from '@/components/Icon';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { contextLists, hasProblemDetails, missingItems, problemMessage, type MissingItem } from '@/components/problemModel';

function FieldErrorList({ errors }: { errors: ApiError['errors'] }) {
  const say = useWording();
  if (errors.length === 0) return null;
  return (
    <ul className="mt-1 flex flex-col gap-0.5">
      {errors.map((e, i) => (
        <li key={i} className="flex gap-2">
          <code className="shrink-0 font-mono text-[11px] opacity-80">{e.pointer}</code>
          <span>{say(e.message)}</span>
        </li>
      ))}
    </ul>
  );
}

function MissingList({ items }: { items: MissingItem[] }) {
  if (items.length === 0) return null;
  return (
    <ul className="mt-1 flex flex-wrap gap-1.5" aria-label="Missing variables">
      {items.map((m, i) => (
        <li key={i} className="rounded-md border border-destructive/30 bg-destructive/10 px-1.5 py-px font-mono text-[11px]">
          {m.key}
          {m.reason ? <span className="opacity-70"> · {m.reason}</span> : null}
        </li>
      ))}
    </ul>
  );
}

function ProblemDetails({ apiError, ctx }: { apiError: ApiError; ctx: Record<string, unknown> }) {
  const missing = missingItems(ctx);
  const lists = contextLists(ctx);
  if (!hasProblemDetails({ errors: apiError.errors.length, missing: missing.length, lists: lists.length, requestId: apiError.requestId })) return null;
  return (
    <AlertDescription className="text-[13px]">
      <FieldErrorList errors={apiError.errors} />
      <MissingList items={missing} />
      {lists.map(([k, v]) => (
        <p key={k} className="mt-1 font-mono text-[11px] opacity-90">
          {k}: {v.join(', ')}
        </p>
      ))}
      {apiError.requestId ? <p className="mt-1 font-mono text-[11px] opacity-70">{apiError.requestId}</p> : null}
    </AlertDescription>
  );
}

/**
 * Inline rendering of an RFC 9457 Problem: the `code` in mono, the message, `errors[]` per pointer and the
 * machine-readable `context` lists (HGR-ENV-MISSING-REQUIRED → `missing[]` keys, HGR-GUARDRAIL-* → `allowedTracks[]`…).
 * Messages that name a provider are reworded for clients (SPEC decision 23).
 */
export function ProblemAlert({ error, className, testId }: { error: unknown; className?: string; testId?: string }) {
  const say = useWording();
  if (!error) return null;
  const apiError = error instanceof ApiError ? error : null;
  return (
    <Alert variant="destructive" data-testid={testId} data-code={apiError?.code} className={cn('px-3 py-2.5', className)}>
      <Icon name="error" size={18} />
      <AlertTitle className="flex flex-wrap items-center gap-2 text-[13px]">
        {apiError ? <code className="rounded-sm bg-destructive/15 px-1.5 py-px font-mono text-[11px]">{apiError.code}</code> : null}
        <span>{say(problemMessage(error, apiError?.message))}</span>
      </AlertTitle>
      {apiError ? <ProblemDetails apiError={apiError} ctx={problemContext(error)} /> : null}
    </Alert>
  );
}
